import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { change, getFormValues, isInvalid, touch } from "redux-form";
import "./paymentModal.css";
import controls from "../../controls";
import { FORM_NAME, RETURN_URL } from "../paymentModal.constants";
import { validateZipByCcHolderCountry } from "../../controls/validation";
import { setPaymentPhase, confirmPaymentAttempt } from "../paymentModal.actions";
import { getPaymentPhase, getPaymentAttemptClientSecret, getPaymentAttemptId } from "../paymentModal.selectors";
import { getUserAddress } from "../../userContext/userContext.selectors";
import { getState } from "../../store";

const {
  components: {
    TextField,
    CountryDropdown,
    StateDropdown,
    ZipFieldContainer: ZipField,
    Button,
  },
  validations: { validateRequired, validateEmail, validateDropdown },
} = controls;


/**
 * Renders <PaymentElement /> plus billing controls (UX parity with ccPaymentForm).
 * Drives the full deferred-intent submit flow:
 *   elements.submit() → /start (gets clientSecret) → stripe.confirmPayment()
 * Phase transitions dispatched into Redux: starting → confirming → polling (or idle on error).
 * Must be mounted inside an <Elements> provider initialized with { mode, amount, currency }.
 */
export const CheckoutForm = ({
  // Passed down from StripeCheckoutStep (deferred-intent inputs)
  orderId,
  sku,
  promoCode,
  ccHolderState,
  cancelPayment,
  amountInCents,
  dispatchStartPaymentAttempt,
  // From Redux connect
  phase,
  attemptId,
  dispatchSetPaymentPhase,
  dispatchConfirmPaymentAttempt,
  addressToCopy,
  changeField,
  formValues,
  formIsInvalid,
  triggerFormValidation,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [confirmError, setConfirmError] = useState(null);

  // Keep the PaymentElement's displayed amount in sync when the cart total changes
  // (e.g. promo code applied or unlimited checkbox toggled).
  useEffect(() => {
    if (elements && amountInCents) {
      elements.update({ amount: amountInCents });
    }
  }, [elements, amountInCents]);

  // "idle" = fresh form, "ready" = /start returned mid-submit (reducer overwrites "starting")
  const isSubmittable = (phase === "idle" || phase === "ready") && stripe && elements;

  const onCopyAddress = () => {
    if (!addressToCopy) return;
    changeField(FORM_NAME, "CcHolderAddress", addressToCopy.Address);
    changeField(FORM_NAME, "CcHolderCountry", addressToCopy.Country);
    changeField(FORM_NAME, "CcHolderState", addressToCopy.State);
    changeField(FORM_NAME, "CcHolderZip", addressToCopy.Zip);
    changeField(FORM_NAME, "CcHolderCity", addressToCopy.City);
    changeField(FORM_NAME, "CcNotifyEmail", addressToCopy.Email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isSubmittable) return;

    // Trigger redux-form field-level validation. If any field is invalid,
    // redux-form will mark them as touched (showing errors) and we bail out.
    triggerFormValidation();
    if (formIsInvalid) return;

    setConfirmError(null);
    dispatchSetPaymentPhase("starting");

    // Step 1: Validate PaymentElement and collect wallet details before hitting /start.
    const { error: submitError } = await elements.submit();
    if (submitError) {
      const message = submitError.message || "Please complete the payment details.";
      setConfirmError(message);
      dispatchSetPaymentPhase("idle");
      return;
    }

    //Step 2
    const ccBillingFields = {
      ccHolderAddress: formValues?.CcHolderAddress || "",
      ccHolderCountry: formValues?.CcHolderCountry || "",
      ccHolderState: formValues?.CcHolderState || ccHolderState || "",
      ccHolderCity: formValues?.CcHolderCity || "",
      ccHolderZip: formValues?.CcHolderZip || "",
      ccNotifyEmail: formValues?.CcNotifyEmail || "",
    };

    // Step 3: Call /start with the current promo code and billing state.
    // doAsync resolves after RECEIVED is dispatched, so getState() has the clientSecret.
    await dispatchStartPaymentAttempt(orderId, sku, promoCode, ccBillingFields);
    const clientSecret = getPaymentAttemptClientSecret(getState());

    if (!clientSecret) {
      // /start failed — reducer already set phase to "failed" via START_PAYMENT_ASYNC.ERROR
      return;
    }

    // Step 4: Confirm with Stripe using the freshly obtained clientSecret.
    dispatchSetPaymentPhase("confirming");
    try {
      const { error } = await stripe.confirmPayment({
        elements,
        clientSecret,
        redirect: "if_required",
        confirmParams: {
          return_url: RETURN_URL,
          payment_method_data: {
            billing_details: {
              address: {
                country: formValues?.CcHolderCountry || "US",
                postal_code: formValues?.CcHolderZip || "",
              },
            },
          },
        },
      });

      console.log("[CheckoutForm] confirmPayment result", { error: error || null }); // CLEANUP
      if (error) {
        const message = error.message || "Payment confirmation failed.";
        setConfirmError(message);
        dispatchSetPaymentPhase("idle", { kind: "confirm", message });
      } else {
        const freshAttemptId = getPaymentAttemptId(getState());
       
       console.log("No error -> attemptId: " + freshAttemptId); // CLEANUP
         // Fire the immediate server confirm; reducer flips to "succeeded" on OrderStatus=="10".
        if (freshAttemptId) {
          dispatchConfirmPaymentAttempt(freshAttemptId);
        }

        console.log("[CheckoutForm] no error → dispatching polling + confirm"); // CLEANUP
        // Start polling as a safety net (handles webhook-only path and network interruptions).
        dispatchSetPaymentPhase("polling");
       
      }
    } catch (err) {
      const message = err.message || "Payment confirmation failed.";
      setConfirmError(message);
      dispatchSetPaymentPhase("idle", { kind: "confirm", message });
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="payment-modal-body">
        <div className="form-widget-header row">
          <div className="col-12 col-md my-auto">Payment Information</div>
        </div>
        <div className="payment-info row mt-3">
          <div className="col-12">
            <PaymentElement options={{ fields: { billingDetails: { address: { country: 'never', postalCode: 'never' } } } }} />
          </div>
        </div>

        <div className="form-widget-header row">
          <div className="col-12 col-md my-auto">Billing Information</div>
          <div className="col text-right">
            <Button
              className="d-inline"
              onClick={onCopyAddress}
              text="Copy Address"
              disabled={false}
              primary={false}
            />
          </div>
        </div>

        <div className="customer-info row mt-3">
          <div className="col-12">
            <TextField
              label="Address"
              name="CcHolderAddress"
              type="text"
              validate={[validateRequired]}
            />
          </div>
          <div className="col-12 col-sm-6 clean-gutter-sm-right">
            <CountryDropdown
              name="CcHolderCountry"
              label="Country"
              formName={FORM_NAME}
              stateFieldName="CcHolderState"
              zipFieldName="CcHolderZip"
              validations={[validateDropdown]}
            />
          </div>
          <div className="col-12 col-sm-6 clean-gutter-sm-left">
            <StateDropdown
              name="CcHolderState"
              label="State"
              formName={FORM_NAME}
              countryFieldName="CcHolderCountry"
              validations={[validateDropdown]}
            />
          </div>
          <div className="col-12 col-sm-6 clean-gutter-sm-right">
            <TextField
              label="City"
              name="CcHolderCity"
              type="text"
              validate={[validateRequired]}
            />
          </div>
          <div className="col-12 col-sm-6 clean-gutter-sm-left">
            <ZipField
              label="Zip"
              name="CcHolderZip"
              countryFieldName="CcHolderCountry"
              formName={FORM_NAME}
              validations={[validateRequired, validateZipByCcHolderCountry]}
            />
          </div>
          <div className="col-12">
            <TextField
              label="Email"
              name="CcNotifyEmail"
              type="text"
              validate={[validateRequired, validateEmail]}
            />
          </div>
        </div>

        {confirmError && (
          <div className="mb-gutter">
            <span className="i2290-inline-error">{confirmError}</span>
          </div>
        )}

        {process.env.NODE_ENV !== "production" && (
          <div style={{ background: "#f0f0f0", border: "1px solid #ccc", borderRadius: 4, padding: "6px 10px", marginBottom: 8, fontSize: 12, fontFamily: "monospace" }}>
            <strong>isSubmittable debug</strong>
            <table style={{ borderCollapse: "collapse", marginTop: 4, width: "100%" }}>
              <tbody>
                <tr>
                  <td style={{ paddingRight: 12 }}>phase === "idle"</td>
                  <td style={{ color: phase === "idle" ? "green" : "red" }}>{String(phase === "idle")} <em style={{ color: "#555" }}>(phase: "{phase}")</em></td>
                </tr>
                <tr>
                  <td style={{ paddingRight: 12 }}>stripe</td>
                  <td style={{ color: !!stripe ? "green" : "red" }}>{String(!!stripe)}</td>
                </tr>
                <tr>
                  <td style={{ paddingRight: 12 }}>elements</td>
                  <td style={{ color: !!elements ? "green" : "red" }}>{String(!!elements)}</td>
                </tr>
                <tr style={{ borderTop: "1px solid #ccc" }}>
                  <td style={{ paddingRight: 12 }}><strong>isSubmittable</strong></td>
                  <td style={{ color: isSubmittable ? "green" : "red" }}><strong>{String(!!isSubmittable)}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <div className="row mt-gutter">
         
            <div className="col clean-gutter-right">
              <Button
                primary={false}
                disabled={false}
                text="Cancel"
                onClick={cancelPayment}
                width="100%"
              />
            </div>
            <div className="col clean-gutter-left">
            <Button
              type="submit"
              primary
              disabled={!isSubmittable}
              text={
                phase === "starting" || phase === "confirming"
                  ? "Processing…"
                  : "Submit to IRS"
              }
              width="100%"
            />
            </div>
         
        </div>
      </div>
    </form>
  );
};

CheckoutForm.propTypes = {
  orderId: PropTypes.number,
  sku: PropTypes.string,
  promoCode: PropTypes.string,
  ccHolderState: PropTypes.string,
  amountInCents: PropTypes.number,
  dispatchStartPaymentAttempt: PropTypes.func.isRequired,
  phase: PropTypes.string.isRequired,
  attemptId: PropTypes.number,
  dispatchSetPaymentPhase: PropTypes.func.isRequired,
  dispatchConfirmPaymentAttempt: PropTypes.func.isRequired,
  addressToCopy: PropTypes.shape({
    Address: PropTypes.string,
    Country: PropTypes.string,
    State: PropTypes.string,
    Zip: PropTypes.string,
    City: PropTypes.string,
    Email: PropTypes.string,
  }),
  changeField: PropTypes.func.isRequired,
  formIsInvalid: PropTypes.bool,
  triggerFormValidation: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  phase: getPaymentPhase(state),
  attemptId: getPaymentAttemptId(state),
  addressToCopy: getUserAddress(state),
  formValues: getFormValues(FORM_NAME)(state) || {},
  formIsInvalid: isInvalid(FORM_NAME)(state),
});

const mapDispatchToProps = (dispatch) => ({
  dispatchSetPaymentPhase: (phase, error) =>
    dispatch(setPaymentPhase(phase, error)),
  dispatchConfirmPaymentAttempt: (attemptId) =>
    dispatch(confirmPaymentAttempt(attemptId)),
  changeField: bindActionCreators({ change }, dispatch).change,
  triggerFormValidation: () => dispatch(touch(
    FORM_NAME,
    "CcHolderAddress",
    "CcHolderCountry",
    "CcHolderState",
    "CcHolderCity",
    "CcHolderZip",
    "CcNotifyEmail",
  )),
});

export default connect(mapStateToProps, mapDispatchToProps)(CheckoutForm);
